import * as React from "react"
import { 
  Create, 
  TabbedForm, 
} from "react-admin"

import ImagesTab from "./ImagesTab"
import BasicInfoTab from "./BasicInfoTab"
import ColorOptionsTab from "./ColorOptionsTab"
import EditableAreasTab from "./EditableAreasTab"
import QuantityDiscountsTab from "./QuantityDiscountsTab"
import SpecificationsTab from "./SpecificationsTab"
import DescriptionTab from "./DescriptionTab"

const ProductTitle = () => {
  return <span>{'建立商品'}</span>;
};

export const validateForm = (values) => {
  const errors = {};

  if (!values.name) {
    errors.name = "ra.validation.required";
  }

  // 如果上架狀態為 OFF (status 為 false 或 0)，則只檢查名稱欄位
  if (values.status === false || values.status === 0) {
    return errors; // 只檢查名稱，其他欄位不檢查
  }

  if (!Array.isArray(values.categories) || values.categories.length === 0) {
    errors.categories = "ra.validation.required";
  }

  if (!values.coverImage?.rawFile && !values.coverImage?.[0]?.title) {
    errors.coverImage = "ra.validation.required";
  }

  if (!values.templateImage?.rawFile && !values.templateImage?.[0]?.title) {
    errors.templateImage = "ra.validation.required";
  }

  if (!values.price) {
    errors.price = "ra.validation.required";
  }

  if (!values.bulkPrice) {
    errors.bulkPrice = "ra.validation.required";
  }

  if (!values.samplingTime) {
    errors.samplingTime = "ra.validation.required";
  }

  if (!values.samplingFee) {
    errors.samplingFee = "ra.validation.required";
  }

  if (!values.blankSampleLeadTimeDays) {
    errors.blankSampleLeadTimeDays = "ra.validation.required";
  }

  if (!values.minQuantity) {
    errors.minQuantity = "ra.validation.required";
  }

  if (!values.maxQuantity) {
    errors.maxQuantity = "ra.validation.required";
  }

  if (!values.incrementStep) {
    errors.incrementStep = "ra.validation.required";
  }

  if (!values.samplingMinOrderQuantity) {
    errors.samplingMinOrderQuantity = "ra.validation.required";
  }

  if (!values.samplingMaxOrderQuantity) {
    errors.samplingMaxOrderQuantity = "ra.validation.required";
  }

  if (!values.samplingIncrementStep) {
    errors.samplingIncrementStep = "ra.validation.required";
  }

  if (!values.bulkMinOrderQuantity) {
    errors.bulkMinOrderQuantity = "ra.validation.required";
  }

  if (!values.bulkMaxOrderQuantity) {
    errors.bulkMaxOrderQuantity = "ra.validation.required";
  }

  if (!values.bulkIncrementStep) {
    errors.bulkIncrementStep = "ra.validation.required";
  }

  // specifications 驗證
  if (!Array.isArray(values.specifications) || values.specifications.length === 0) {
    errors.specifications = "ra.validation.required";
  } else {
    const hasValidSpec = values.specifications.some(spec => !!spec.name);
    if (!hasValidSpec) {
      errors.specifications = "ra.validation.required";
    }
  }

  // printMethods 驗證
  if (!Array.isArray(values.printMethods) || values.printMethods.length === 0) {
    errors.printMethods = "ra.validation.required";
  } else {
    const ids = values.printMethods
      .map(m => m?.printingMethodId)
      .filter(id => id != null);

    const hasValidMethod = ids.length > 0;
    if (!hasValidMethod) {
      errors.printMethods = "ra.validation.required";
    } else {
      // 檢查是否有重複
      const uniqueIds = new Set(ids);
      if (uniqueIds.size !== ids.length) {
        errors.printMethods = "已選的印製方式有重複";
      }
    }
  }

  // quantityDiscounts 驗證
  if (Array.isArray(values.quantityDiscounts)) {
    const discountsErrors = [];
    values.quantityDiscounts.forEach((item, index) => {
      if (item.minQuantity && item.maxQuantity) {
        const itemErrors = {};
        if (!item.discountPrice) {
          itemErrors.discountPrice = "ra.validation.required";
        }
        if (!item.leadTimeDays) {
          itemErrors.leadTimeDays = "ra.validation.required";
        }
        if (Object.keys(itemErrors).length > 0) {
          discountsErrors[index] = itemErrors;
        }
      }
    });
    if (discountsErrors.length > 0) {
      errors.quantityDiscounts = discountsErrors;
    }
  }

  // editableAreas 驗證
  if (!Array.isArray(values.editableAreas) || values.editableAreas.length === 0) {
    errors.editableAreas = "ra.validation.required";
  } else {
    const editableErrors = [];
    let hasValid = false;

    values.editableAreas.forEach((area, i) => {
      const err = {};

      if (!area.areaName) return;

      if (!area.templateImage?.rawFile && !area.templateImage?.[0]?.title) {
        err.templateImage = "ra.validation.required";
      }

      if (Array.isArray(area.images)) {
        const imageErrors = [];
        let hasImageError = false;

        area.images.forEach((img, j) => {
          if (!img.image?.rawFile && !img.image?.[0]?.title) {
            imageErrors[j] = { image: "ra.validation.required" };
            hasImageError = true;
          }
        });

        if (hasImageError) {
          err.images = imageErrors;
        }
      }

      if (Object.keys(err).length > 0) {
        editableErrors[i] = err;
      } else {
        hasValid = true;
      }
    });

    if (!hasValid) {
      errors.editableAreas = "ra.validation.required";
    } else if (editableErrors.length > 0) {
      errors.editableAreas = editableErrors;
    }
  }

  return errors;
};

const ProductCreate = () => {
  return (
    <Create title={<ProductTitle/>}>
      <TabbedForm 
        defaultValues={{
          sorting: 1,
          code: "",
          name: "",
          categories: [],
          price: "",
          stock: 0,
          minOrderQuantity: 1,
          maxOrderQuantity: "",
          samplingTime: "",
          samplingFee: "",
          minQuantity: 1,
          maxQuantity: 10,
          incrementStep: 1,
          description: "",
          specification: "",
          status: 1,
          coverImage: [],
          templateImage: [],
          colors: [
            {
              name: "",
              // code: "",
              navImage: [],
              // image: [],
            }
          ],
          specifications: [
            {
              name: "",
            }
          ],
          quantityDiscounts: [
            {
              minQuantity: "",
              maxQuantity: "",
              discountPercentage: "",
              leadTimeDays: "",
              discountPrice: "",
            }
          ],
          editableAreas: [
            {
              areaName: "",
              templateImage: "",
              images: [
                {
                  name: "",
                  image: [],
                }
              ]
            }
          ],
          printMethods: [
            {
              printing_method_id: ""
            }
          ],
          isLogoColor: false,
          logoColor: "#000000",
          logoOpacity: 1,
        }}
        validate={validateForm}
      >
        <TabbedForm.Tab
          label="resources.products.tabs.image"
          sx={{ maxWidth: "50em" }}
        >
          <ImagesTab />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          label="基本資料"
          path="basic"
          sx={{ maxWidth: "50em" }}
        >
          <BasicInfoTab />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          label="顏色選項"
          path="color-options"
          sx={{ maxWidth: "50em" }}
        >
          <ColorOptionsTab />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          label="規格選項"
          path="specification-options"
          sx={{ maxWidth: "50em" }}
        >
          <SpecificationsTab />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          label="數量折扣規則"
          path="quantity-discounts"
          sx={{ maxWidth: "50em" }}
        >
          <QuantityDiscountsTab />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          label="編輯區域"
          path="editable-areas"
          sx={{ maxWidth: "50em" }}
        >
          <EditableAreasTab />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          label="描述"
          path="description"
          sx={{ maxWidth: "50em" }}
        >
          <DescriptionTab />
        </TabbedForm.Tab>
      </TabbedForm>
    </Create>
  )
}

export default ProductCreate